/**
 * @fileoverview Client container — routes the active view to the right
 *               view component. Adapted from upstream:
 *  - View is read from context (`useCalendar().view`) instead of a prop
 *    so the header's view-switcher can update without re-mounting.
 *  - Drag-and-drop wrapper removed (deferred to v1.1).
 *  - All-day events ((end - start) >= ~23h on the same calendar day)
 *    are pushed into `multiDayEvents` so they render as a row above the
 *    time grid in week/day views, mirroring how Google Calendar treats
 *    "all-day" entries.
 */

import { useMemo, type ReactNode } from "react";
import { isSameDay, parseISO } from "date-fns";

import { useCalendar } from "@/calendar/contexts/use-calendar";
import { placeEventsByDateMode } from "@/calendar/eventAdapter";

import { CalendarHeader } from "@/calendar/components/header/calendar-header";
import { CalendarYearView } from "@/calendar/components/year-view/calendar-year-view";
import { CalendarMonthView } from "@/calendar/components/month-view/calendar-month-view";
import { CalendarAgendaView } from "@/calendar/components/agenda-view/calendar-agenda-view";
import { CalendarDayView } from "@/calendar/components/week-and-day-view/calendar-day-view";
import { CalendarWeekView } from "@/calendar/components/week-and-day-view/calendar-week-view";
import { CalendarListView } from "@/calendar/components/list-view/calendar-list-view";
import { CalendarFeedSettings } from "@/calendar/components/settings-view/calendar-feed-settings";

import type { KeyDateCategory } from "@cvx/keyDates/jobKeyDates/schema";

interface ClientContainerProps {
  /**
   * Categories the user wants visible. Pass-through to the header's
   * category filter popover. When undefined the filter is hidden and
   * every event is shown.
   */
  visibleCategories?: ReadonlyArray<KeyDateCategory>;
  onVisibleCategoriesChange?: (next: KeyDateCategory[]) => void;
  /**
   * Hide subtasks (rows nested under a parent task) toggle (MOR-752).
   * Controlled by the host page like the category filter; when the setter is
   * undefined the toggle is hidden and subtasks always render.
   */
  hideSubtasks?: boolean;
  onHideSubtasksChange?: (next: boolean) => void;
  hideAddButton?: boolean;
  /** When true, show the "Subscribe" (ICS feed) button in the header. */
  showSubscribe?: boolean;
  /**
   * Optional content rendered at the top of the calendar header card.
   * Threaded straight through to `CalendarHeader` (workspace page title).
   */
  headerSlot?: ReactNode;
}

export function ClientContainer({
  visibleCategories,
  onVisibleCategoriesChange,
  hideSubtasks,
  onHideSubtasksChange,
  hideAddButton,
  showSubscribe,
  headerSlot,
}: ClientContainerProps = {}) {
  const { view, selectedDate, selectedUserId, events, dateMode } =
    useCalendar();

  // Re-place the stream on due / planned / both before any windowing so the
  // toggle controls which date each event lands on (MOR-696).
  const placedEvents = useMemo(
    () => placeEventsByDateMode(events, dateMode),
    [events, dateMode],
  );

  // Subtask visibility runs first — it's a row-level cut, independent of
  // where the row was placed (MOR-752).
  const subtaskFilteredEvents = useMemo(() => {
    if (!hideSubtasks) return placedEvents;
    return placedEvents.filter((event) => !event.meta?.parentTaskId);
  }, [placedEvents, hideSubtasks]);

  // Category filter runs next (it shrinks the set the date-window
  // filter operates on). Undefined means "no filter — show all".
  const categoryFilteredEvents = useMemo(() => {
    if (!visibleCategories) return subtaskFilteredEvents;
    const allowed = new Set(visibleCategories);
    return subtaskFilteredEvents.filter((event) =>
      event.meta?.category ? allowed.has(event.meta.category) : true,
    );
  }, [subtaskFilteredEvents, visibleCategories]);

  const filteredEvents = useMemo(() => {
    return categoryFilteredEvents.filter((event) => {
      const eventStartDate = parseISO(event.startDate);
      const eventEndDate = parseISO(event.endDate);

      // List view ignores the date window — every event is in scope so
      // the practitioner can scroll back over the full history of the
      // matter without scrubbing the navigator.
      if (view === "list") {
        return selectedUserId === "all" || event.user.id === selectedUserId;
      }

      if (view === "year") {
        const yearStart = new Date(selectedDate.getFullYear(), 0, 1);
        const yearEnd = new Date(
          selectedDate.getFullYear(),
          11,
          31,
          23,
          59,
          59,
          999,
        );
        const isInSelectedYear =
          eventStartDate <= yearEnd && eventEndDate >= yearStart;
        const isUserMatch =
          selectedUserId === "all" || event.user.id === selectedUserId;
        return isInSelectedYear && isUserMatch;
      }

      if (view === "month" || view === "agenda") {
        const monthStart = new Date(
          selectedDate.getFullYear(),
          selectedDate.getMonth(),
          1,
        );
        const monthEnd = new Date(
          selectedDate.getFullYear(),
          selectedDate.getMonth() + 1,
          0,
          23,
          59,
          59,
          999,
        );
        const isInSelectedMonth =
          eventStartDate <= monthEnd && eventEndDate >= monthStart;
        const isUserMatch =
          selectedUserId === "all" || event.user.id === selectedUserId;
        return isInSelectedMonth && isUserMatch;
      }

      if (view === "week") {
        const dayOfWeek = selectedDate.getDay();
        const weekStart = new Date(selectedDate);
        weekStart.setDate(selectedDate.getDate() - dayOfWeek);
        weekStart.setHours(0, 0, 0, 0);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        weekEnd.setHours(23, 59, 59, 999);
        const isInSelectedWeek =
          eventStartDate <= weekEnd && eventEndDate >= weekStart;
        const isUserMatch =
          selectedUserId === "all" || event.user.id === selectedUserId;
        return isInSelectedWeek && isUserMatch;
      }

      if (view === "day") {
        const dayStart = new Date(
          selectedDate.getFullYear(),
          selectedDate.getMonth(),
          selectedDate.getDate(),
          0,
          0,
          0,
        );
        const dayEnd = new Date(
          selectedDate.getFullYear(),
          selectedDate.getMonth(),
          selectedDate.getDate(),
          23,
          59,
          59,
        );
        const isInSelectedDay =
          eventStartDate <= dayEnd && eventEndDate >= dayStart;
        const isUserMatch =
          selectedUserId === "all" || event.user.id === selectedUserId;
        return isInSelectedDay && isUserMatch;
      }

      return false;
    });
  }, [selectedDate, selectedUserId, categoryFilteredEvents, view]);

  const singleDayEvents = filteredEvents.filter((event) => {
    const startDate = parseISO(event.startDate);
    const endDate = parseISO(event.endDate);
    // Treat genuine all-day events (Morgan key dates) as multi-day so
    // they render in the top row of week/day views rather than as a
    // 24-hour tall block in the time grid.
    if (event.meta?.allDay) return false;
    return isSameDay(startDate, endDate);
  });

  const multiDayEvents = filteredEvents.filter((event) => {
    const startDate = parseISO(event.startDate);
    const endDate = parseISO(event.endDate);
    if (event.meta?.allDay) return true;
    return !isSameDay(startDate, endDate);
  });

  // For year view, we only care about the start date — by using the
  // same date for both start and end, only the start day shows a dot.
  const eventStartDates = useMemo(() => {
    return filteredEvents.map((event) => ({
      ...event,
      endDate: event.startDate,
    }));
  }, [filteredEvents]);

  return (
    <div className="overflow-hidden rounded-xl border bg-background">
      <CalendarHeader
        events={filteredEvents}
        visibleCategories={visibleCategories}
        onVisibleCategoriesChange={onVisibleCategoriesChange}
        hideSubtasks={hideSubtasks}
        onHideSubtasksChange={onHideSubtasksChange}
        hideAddButton={hideAddButton}
        showSubscribe={showSubscribe}
        headerSlot={headerSlot}
      />

      {view === "day" && (
        <CalendarDayView
          singleDayEvents={singleDayEvents}
          multiDayEvents={multiDayEvents}
        />
      )}
      {view === "month" && (
        <CalendarMonthView
          singleDayEvents={singleDayEvents}
          multiDayEvents={multiDayEvents}
        />
      )}
      {view === "week" && (
        <CalendarWeekView
          singleDayEvents={singleDayEvents}
          multiDayEvents={multiDayEvents}
        />
      )}
      {view === "year" && <CalendarYearView allEvents={eventStartDates} />}
      {view === "agenda" && (
        <CalendarAgendaView
          singleDayEvents={singleDayEvents}
          multiDayEvents={multiDayEvents}
        />
      )}
      {view === "list" && <CalendarListView events={filteredEvents} />}
      {view === "settings" && <CalendarFeedSettings />}
    </div>
  );
}
